import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { buildColumnMapping, type ColumnDef, type EntityConfig } from '@/pages/import/schema';
import { parseSheet, normalizeDateString } from '@/pages/import/utils';
import ConflictModal, { type ConflictRow } from './ConflictModal';
import { useOffice } from '@/hooks/useOffice';

interface Props {
  entity: EntityConfig;
  onImported: () => void;
}

type ImportMode = 'add' | 'replace';

interface Status {
  type: 'idle' | 'error' | 'success';
  msg: string;
}

interface Result {
  inserted: number;
  updated: number;
  skipped: number;
}

interface PendingConflict {
  mediaLabel: string;
  payload: Record<string, any>;
}

export default function ImportPanel({ entity, onImported }: Props) {
  const { currentOfficeId } = useOffice();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>({ type: 'idle', msg: '' });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<ImportMode>('add');
  const [pendingConflicts, setPendingConflicts] = useState<PendingConflict[] | null>(null);
  const [pendingNewRows, setPendingNewRows] = useState<Record<string, any>[] | null>(null);
  const [pendingSkipped, setPendingSkipped] = useState(0);

  const handleFile = async (file: File) => {
    setResult(null);
    setStatus({ type: 'idle', msg: '' });
    try {
      const parsed = await parseSheet(file);
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      const map = buildColumnMapping(parsed.headers, entity.columns);
      setMapping(map);
      const missingRequired = entity.columns.filter((c) => c.required && !map[c.key]);
      if (missingRequired.length > 0) {
        setStatus({ type: 'error', msg: `Missing required column(s): ${missingRequired.map((c) => c.label).join(', ')}` });
      }
    } catch (e: any) {
      setStatus({ type: 'error', msg: 'Could not read file: ' + (e?.message || 'unknown error') });
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // Convert raw rows into insert-ready payloads, applying column type coercion.
  const buildValid = () => {
    const valid: Record<string, any>[] = [];
    let skipped = 0;

    rows.forEach((row) => {
      const payload: Record<string, any> = {};
      let ok = true;
      entity.columns.forEach((col) => {
        const header = mapping[col.key];
        if (!header) return;
        const rawValue = row[header];
        const v = rawValue == null ? '' : String(rawValue).trim();

        if (col.key === 'created_at') {
          if (v) {
            const d = new Date(v);
            payload[col.key] = Number.isNaN(d.getTime()) ? null : d.toISOString();
          }
          return;
        }
        if (col.key === 'modified_date') {
          payload[col.key] = normalizeDateString(v);
          return;
        }
        if (col.type === 'boolean') {
          payload[col.key] = v === '' ? false : ['true', '1', 'yes', 'y', 't', 'on'].includes(v.toLowerCase());
          return;
        }
        if (col.type === 'number') {
          if (v === '') { payload[col.key] = null; return; }
          const n = Number(v);
          payload[col.key] = Number.isNaN(n) ? null : n;
          return;
        }
        if (col.required && v === '') { ok = false; return; }
        payload[col.key] = v === '' ? null : v;
      });
      if (ok) {
        payload.office_id = currentOfficeId;
        valid.push(payload);
      }
      else skipped++;
    });

    return { valid, skipped };
  };

  // Insert in small chunks so large files never hit a gateway timeout. If a
  // whole chunk is rejected, fall back to one-at-a-time inserts.
  const insertChunked = async (list: Record<string, any>[]) => {
    const CHUNK = 100;
    let inserted = 0;
    let firstError: string | null = null;

    for (let i = 0; i < list.length && !firstError; i += CHUNK) {
      const chunk = list.slice(i, i + CHUNK);
      const { error } = await supabase.from(entity.table).insert(chunk);
      if (error) {
        let chunkInserted = 0;
        for (const row of chunk) {
          const one = await supabase.from(entity.table).insert(row);
          if (one.error) {
            firstError = one.error.message;
            break;
          }
          chunkInserted++;
        }
        inserted += chunkInserted;
      } else {
        inserted += chunk.length;
      }
    }

    return { inserted, firstError };
  };

  const doImport = async () => {
    setImporting(true);
    setResult(null);

    const { valid, skipped } = buildValid();
    if (valid.length === 0) {
      setImporting(false);
      setStatus({ type: 'error', msg: 'No valid rows to import.' });
      return;
    }

    // Replace mode: wipe the table first, then insert everything.
    if (mode === 'replace') {
      setStatus({ type: 'idle', msg: 'Clearing existing data...' });
      const { error: clearErr } = await supabase.from(entity.table).delete().eq('office_id', currentOfficeId);
      if (clearErr) {
        setImporting(false);
        setStatus({ type: 'error', msg: `Failed to clear existing data: ${clearErr.message}` });
        return;
      }

      const { inserted, firstError } = await insertChunked(valid);
      setImporting(false);
      if (firstError) {
        setStatus({ type: 'error', msg: firstError });
        return;
      }
      setResult({ inserted, updated: 0, skipped });
      setStatus({ type: 'success', msg: 'Import complete' });
      onImported();
      return;
    }

    // Add mode: append, but detect duplicate Media Labels against the DB first.
    const { data: existingData, error: exErr } = await supabase
      .from(entity.table)
      .select('media_label')
      .eq('office_id', currentOfficeId);
    if (exErr) {
      setImporting(false);
      setStatus({ type: 'error', msg: exErr.message });
      return;
    }

    const existingLabels = new Set<string>(
      ((existingData || []) as { media_label: string }[]).map((r) => r.media_label)
    );

    const conflicts: PendingConflict[] = [];
    const newRows: Record<string, any>[] = [];
    valid.forEach((p) => {
      if (existingLabels.has(p.media_label)) {
        conflicts.push({ mediaLabel: p.media_label, payload: p });
      } else {
        newRows.push({ ...p, is_new: true });
      }
    });

    // No duplicates at all — just insert and finish.
    if (conflicts.length === 0) {
      const { inserted, firstError } = await insertChunked(newRows);
      setImporting(false);
      if (firstError) {
        setStatus({ type: 'error', msg: firstError });
        return;
      }
      setResult({ inserted, updated: 0, skipped });
      setStatus({ type: 'success', msg: 'Import complete' });
      onImported();
      return;
    }

    // Pause and let the user decide how to handle duplicates.
    setPendingConflicts(conflicts);
    setPendingNewRows(newRows);
    setPendingSkipped(skipped);
    setImporting(false);
  };

  const finalizeImport = async (overwriteLabels: string[]) => {
    const conflicts = pendingConflicts;
    const newRows = pendingNewRows;
    const skipped = pendingSkipped;
    setPendingConflicts(null);
    setPendingNewRows(null);
    if (!conflicts || !newRows) return;

    setImporting(true);
    setResult(null);

    const overwriteSet = new Set(overwriteLabels);
    let inserted = 0;
    let updated = 0;
    let skippedCount = skipped;

    // Insert all non-conflicting rows first.
    const { inserted: ins, firstError } = await insertChunked(newRows);
    inserted = ins;
    if (firstError) {
      setImporting(false);
      setStatus({ type: 'error', msg: firstError });
      return;
    }

    // Handle each duplicate: overwrite (update) or skip.
    let updateErr: string | null = null;
    for (const c of conflicts) {
      if (overwriteSet.has(c.mediaLabel)) {
        const { error } = await supabase
          .from(entity.table)
          .update(c.payload)
          .eq('media_label', c.mediaLabel)
          .eq('office_id', currentOfficeId);
        if (error) {
          updateErr = error.message;
          break;
        }
        updated++;
      } else {
        skippedCount++;
      }
    }

    setImporting(false);
    if (updateErr) {
      setStatus({ type: 'error', msg: updateErr });
      return;
    }
    setResult({ inserted, updated, skipped: skippedCount });
    setStatus({ type: 'success', msg: 'Import complete' });
    onImported();
  };

  const hasFile = headers.length > 0 || rows.length > 0;
  const previewRows = rows.slice(0, 5);
  const validCount = rows.length;

  return (
    <div>
      {!hasFile ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
            dragOver ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'
          }`}
        >
          <i className="ri-upload-cloud-2-line w-8 h-8 flex items-center justify-center mx-auto text-slate-400 text-2xl" />
          <div className="text-sm font-medium mt-3">Drop a file here, or click to browse</div>
          <div className="text-xs text-slate-500 mt-1">Supports .csv, .xlsx, .xls</div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-file-excel-2-line w-4 h-4 flex items-center justify-center text-slate-500 shrink-0" />
              <span className="text-sm font-medium truncate">{fileName}</span>
              <span className="text-xs text-slate-500 whitespace-nowrap">{validCount} row{validCount === 1 ? '' : 's'}</span>
            </div>
            <button
              onClick={() => { setFileName(''); setHeaders([]); setRows([]); setMapping({}); setStatus({ type: 'idle', msg: '' }); setResult(null); }}
              className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer whitespace-nowrap"
            >
              Choose another file
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {entity.columns.map((col) => {
              const mapped = mapping[col.key];
              return (
                <span
                  key={col.key}
                  className={`px-2 py-1 text-xs rounded-md inline-flex items-center gap-1 ${
                    mapped ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <i className={`${mapped ? 'ri-check-line' : 'ri-close-line'} w-3 h-3 flex items-center justify-center`} />
                  {col.label}
                  {col.required && <span className="text-red-400">*</span>}
                </span>
              );
            })}
          </div>

          <div className="border border-slate-200 rounded-md overflow-x-auto mb-3">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  {entity.columns.map((col) => (
                    <th key={col.key} className="text-left px-3 py-2 font-medium whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {entity.columns.map((col) => {
                      const header = mapping[col.key];
                      const v = header ? row[header] : '';
                      return (
                        <td key={col.key} className="px-3 py-1.5 text-slate-600 whitespace-nowrap max-w-[200px] truncate">
                          {v == null || v === '' ? '—' : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {previewRows.length === 0 && (
                  <tr><td colSpan={entity.columns.length} className="px-3 py-4 text-center text-slate-400">No data rows in this file</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mb-3">
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setMode('add')}
                className={`px-4 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition ${
                  mode === 'add' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Add Import
              </button>
              <button
                onClick={() => setMode('replace')}
                className={`px-4 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition ${
                  mode === 'replace' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Replace All
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {mode === 'add'
                ? "Append new tapes to the existing database. If a Media Label already exists, you'll be asked to overwrite or skip it."
                : 'Delete all existing tapes first, then import the file as a fresh set.'}
            </p>
          </div>

          {status.type !== 'idle' && (
            <div className={`mb-3 px-3 py-2 rounded-md text-xs ${
              status.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}>
              {status.msg}
            </div>
          )}

          {result && (
            <div className="mb-3 px-3 py-2 rounded-md text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
              Added {result.inserted} record{result.inserted === 1 ? '' : 's'}
              {result.updated > 0 ? `, updated ${result.updated}` : ''}
              {result.skipped > 0 ? `, skipped ${result.skipped}` : ''}.
            </div>
          )}

          <button
            onClick={doImport}
            disabled={importing || validCount === 0 || entity.columns.some((c) => c.required && !mapping[c.key])}
            className="w-full px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {importing
              ? 'Importing...'
              : mode === 'replace'
                ? `Replace with ${validCount} ${entity.singular}${validCount === 1 ? '' : 's'}`
                : `Add ${validCount} ${entity.singular}${validCount === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {pendingConflicts && (
        <ConflictModal
          conflicts={pendingConflicts.map((c) => ({ mediaLabel: c.mediaLabel }) as ConflictRow)}
          onCancel={() => { setPendingConflicts(null); setPendingNewRows(null); }}
          onConfirm={finalizeImport}
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        onChange={onPick}
        className="hidden"
      />
    </div>
  );
}
